import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { UserInfo } from '@/types'
import { clearCompany } from '@/utils/company'

export const useUserStore = defineStore('user', () => {
  const token = ref(localStorage.getItem('token') || '')
  const userInfo = ref<UserInfo | null>(
    (() => {
      try {
        const saved = localStorage.getItem('userInfo')
        return saved ? JSON.parse(saved) : null
      } catch { return null }
    })()
  )
  const permissions = ref<string[]>(userInfo.value?.permissions || [])
  const roles = ref<string[]>(userInfo.value?.roles || [])

  const isLoggedIn = computed(() => !!token.value)

  function setToken(val: string) {
    token.value = val
    localStorage.setItem('token', val)
  }

  function setUserInfo(info: UserInfo) {
    userInfo.value = info
    permissions.value = info.permissions || []
    roles.value = info.roles || []
    localStorage.setItem('userInfo', JSON.stringify(info))
  }

  /** Check if a single permission is granted (supports wildcards) */
  function hasPermission(perm: string): boolean {
    const perms = permissions.value
    // Full access
    if (perms.includes('*')) return true
    // Exact match
    if (perms.includes(perm)) return true
    // Wildcard: "purchase:*" matches "purchase:order:view"
    const module = perm.split(':')[0]
    if (perms.includes(module + ':*')) return true
    // Module-level bare permission: "purchase" matches "purchase:order:view"
    if (perms.includes(module)) return true
    return false
  }

  /** Check if any of the given permissions is granted */
  function hasAnyPermission(requiredPerms: string[]): boolean {
    return requiredPerms.some((p) => hasPermission(p))
  }

  function logout() {
    token.value = ''
    userInfo.value = null
    permissions.value = []
    roles.value = []
    localStorage.removeItem('token')
    localStorage.removeItem('userInfo')
    clearCompany()
    window.location.replace('/login')
  }

  return { token, userInfo, permissions, roles, isLoggedIn, setToken, setUserInfo, hasPermission, hasAnyPermission, logout }
})
